import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye, EyeOff, KeyRound, Loader2, Lock, LogIn, Mail, Phone, ShieldCheck, User, UserPlus, X,
} from 'lucide-react';
import { authService } from '@kss/api';
import { useAuth } from '@kss/auth';
import { isEmail, isPhone } from '@kss/ui';

/**
 * Global login popup — replaces the standalone /login page on the
 * landing site. Listens for `window` event `kss:open-login` so any
 * navbar button or `RequireAuth` redirect can request it.
 *
 * Three tabs:
 *   - Sign in:        tenant code + username + password (with show/hide)
 *   - Sign up:        name + email + phone + tenant + password (validates)
 *   - Forgot:         email + tenant → reset link toast → flips to Sign in
 *
 * Plus four social-login affordances (Gmail / Twitter / Facebook /
 * LinkedIn / YouTube) — wired to the OAuth-redirect endpoints when the
 * backend exposes them; for now they pop a "coming soon" toast so the
 * UI can ship ahead of the SSO work.
 */

type Tab = 'signin' | 'signup' | 'forgot';

interface OpenEventDetail {
  tab?: Tab;
  redirect?: string;
}

declare global {
  interface WindowEventMap {
    'kss:open-login': CustomEvent<OpenEventDetail>;
  }
}

/**
 * Resolves the most-likely tenant code from the current hostname.
 * Used as the placeholder for the Company name field so users
 * landing on `bill.kristysoftware.com` see `bill` (not `TNT-0001`)
 * and don't accidentally type the placeholder text as the value.
 */
function tenantHintFromHostname(): string {
  if (typeof window === 'undefined') return 'company';
  const host = window.location.hostname.split('.')[0]?.toLowerCase() ?? '';
  if (!host || host === 'www' || host === 'localhost' || host === 'app') return 'company';
  return host;
}

export default function LoginModal() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('signin');
  const [redirectAfter, setRedirectAfter] = useState<string>('/panel/dashboard');

  // Open from anywhere via custom event.
  useEffect(() => {
    function onOpen(e: CustomEvent<OpenEventDetail>) {
      const detail = e.detail || {};
      if (detail.tab) setTab(detail.tab);
      if (detail.redirect) setRedirectAfter(detail.redirect);
      setOpen(true);
    }
    window.addEventListener('kss:open-login', onOpen as EventListener);
    return () => window.removeEventListener('kss:open-login', onOpen as EventListener);
  }, []);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    // Outer overlay scrolls on tall content (e.g. SignUp form with all
    // KYC fields visible on a short viewport). `items-start` keeps the
    // dialog at the top of the scroll area instead of forcing centred
    // alignment to clip off-screen.
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <Header tab={tab} onClose={() => setOpen(false)} />
        <Tabs tab={tab} onChange={setTab} />
        {/* Body scrolls independently so the header + tab strip stay
            pinned while a long form (Sign-Up with address fields)
            scrolls inside. */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {tab === 'signin' && (
            <SignInForm
              onClose={() => setOpen(false)}
              redirectAfter={redirectAfter}
              onSwitchTab={setTab}
            />
          )}
          {tab === 'signup' && <SignUpForm onSwitchTab={setTab} />}
          {tab === 'forgot' && <ForgotForm onSwitchTab={setTab} />}

          <SocialBlock />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Header + tab strip                                                  */
/* -------------------------------------------------------------------- */

function Header({ tab, onClose }: { tab: Tab; onClose: () => void }) {
  const titles: Record<Tab, { t: string; s: string }> = {
    signin: { t: 'Welcome back', s: 'Sign in to your KSS BillBook tenant.' },
    signup: { t: 'Create your tenant', s: 'Get going with a free 14-day trial — no card needed.' },
    forgot: { t: 'Reset your password', s: 'We’ll email a reset link in under a minute.' },
  };
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[var(--color-border)] px-6 py-5">
      <div>
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-[var(--color-primary)] text-white">
            <KeyRound size={14} />
          </span>
          <h2 className="text-base font-semibold">{titles[tab].t}</h2>
        </div>
        <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{titles[tab].s}</p>
      </div>
      <button
        type="button"
        className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        onClick={onClose}
        aria-label="Close"
      >
        <X size={18} />
      </button>
    </div>
  );
}

function Tabs({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  const items: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'signin', label: 'Sign in', icon: <LogIn size={13} /> },
    { id: 'signup', label: 'Sign up', icon: <UserPlus size={13} /> },
    { id: 'forgot', label: 'Forgot', icon: <ShieldCheck size={13} /> },
  ];
  return (
    <div className="grid grid-cols-3 border-b border-[var(--color-border)]">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          className={`flex items-center justify-center gap-1.5 px-3 py-3 text-sm transition-colors ${
            tab === it.id
              ? 'border-b-2 border-[var(--color-primary)] font-medium text-[var(--color-primary)]'
              : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          {it.icon} {it.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Sign-in form                                                        */
/* -------------------------------------------------------------------- */

function SignInForm({
  onClose,
  redirectAfter,
  onSwitchTab,
}: {
  onClose: () => void;
  redirectAfter: string;
  onSwitchTab: (t: Tab) => void;
}) {
  const navigate = useNavigate();
  const { login } = useAuth();
  // Company-name input visibility — same rule as the panel login:
  //   - LOCAL dev (`import.meta.env.DEV`) → ALWAYS shown so the developer
  //     can type the tenant on localhost (no subdomain to derive from).
  //   - LIVE build → hidden; the tenant comes from the URL subdomain
  //     (bill.kristysoftware.com → "bill").
  //   - `VITE_SHOW_COMPANY_INPUT=Y` force-shows it in any environment.
  const isDevServer = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  const showCompanyInput =
    isDevServer ||
    /^Y(es)?$|^true$|^1$/i.test(
      ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SHOW_COMPANY_INPUT ?? '').trim(),
    );
  const hostnameTenant = tenantHintFromHostname();
  const [tenantCode, setTenantCode] = useState(showCompanyInput ? '' : hostnameTenant);
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  // MFA second-step — mirrors apps/panel/src/pages/Login.tsx. When the
  // server responds with 401 `{ error: "mfa_required" }`, we keep the
  // credentials the user just typed and surface a TOTP/recovery input
  // pair. On the next submit we re-send everything including the
  // chosen MFA field. See BACKEND_AGENT_PROMPTS.md §16 for the contract.
  const [mfaChallenge, setMfaChallenge] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (mfaChallenge && !totpCode.trim() && !recoveryCode.trim()) {
      setErr('Enter your 6-digit code or a recovery code to continue.');
      return;
    }
    if (mfaChallenge && totpCode.trim() && recoveryCode.trim()) {
      setErr('Provide TOTP or recovery code, not both.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        tenantCode,
        userName,
        password,
        ...(mfaChallenge && totpCode.trim() ? { totpCode: totpCode.trim() } : {}),
        ...(mfaChallenge && recoveryCode.trim() ? { recoveryCode: recoveryCode.trim() } : {}),
      };
      const r = (await authService.login(payload)) as unknown as {
        accessToken: string;
        refreshToken: string;
        expiresAt: string;
        sessionId: string;
        user: { id: number | string; username: string; email?: string };
        tenant: { tenantId: string; code: string; name?: string };
        company?: { id: number | string; code: string; name: string; isHeadOffice?: boolean } | null;
        roles?: string[];
      };
      const session = {
        accessToken: r.accessToken,
        refreshToken: r.refreshToken,
        expiresAt: r.expiresAt,
        tenantId: r.tenant.tenantId,
        // Capture tenantCode so the silent-refresh interceptor can
        // include it in /api/Auth/refresh body (required by live API
        // — see §25).
        tenantCode: r.tenant.code ?? tenantCode,
        sessionId: r.sessionId,
      };
      login(
        {
          id: String(r.user.id),
          name: r.user.username,
          email: r.user.email ?? '',
          role: r.roles?.[0] ?? 'User',
          tenantId: r.tenant.tenantId,
          // §13.1: capture the new HQ-company block so the chrome can
          // display the legal-entity name instead of bare tenant code.
          company: r.company ?? null,
        },
        session,
      );
      onClose();
      navigate(redirectAfter || '/panel/dashboard');
    } catch (e) {
      const errResp = e as {
        response?: { status?: number; data?: { error?: string; message?: string } };
        message?: string;
      };
      const code = errResp?.response?.data?.error ?? '';
      const msg = errResp?.response?.data?.message ?? errResp?.message ?? 'Sign-in failed';
      // Server signals "your credentials are valid, but we need a 2FA
      // code now". Flip into the MFA challenge step and keep the
      // username + password so the user doesn't re-type anything.
      if (errResp?.response?.status === 401 && /^mfa[_-]?required$/i.test(code)) {
        setMfaChallenge(true);
        setErr(null);
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {err && <FormError msg={err} />}

      {showCompanyInput ? (
        <Field
          label="Company name"
          icon={<ShieldCheck size={13} />}
          value={tenantCode}
          onChange={setTenantCode}
          placeholder={tenantHintFromHostname()}
          required
          autoFocus={!mfaChallenge}
          disabled={mfaChallenge}
          hint={`Your company short code (e.g. ${tenantHintFromHostname()}). Same as the prefix of your panel URL.`}
        />
      ) : (
        // Hidden by default — tenant comes from subdomain. Flip with
        // VITE_SHOW_COMPANY_INPUT=Y if you need a visible textbox.
        <p className="-mb-1 text-[11px] text-[var(--color-text-muted)]">
          Signing in to <strong className="text-[var(--color-text-primary)]">{tenantCode || hostnameTenant}</strong>{' '}
          (from this URL).
        </p>
      )}
      <Field
        label="Username or email"
        icon={<User size={13} />}
        value={userName}
        onChange={setUserName}
        placeholder="admin / you@company.com"
        required
        disabled={mfaChallenge}
      />
      <Field
        label="Password"
        icon={<Lock size={13} />}
        value={password}
        onChange={setPassword}
        type={showPassword ? 'text' : 'password'}
        placeholder="••••••••"
        required
        disabled={mfaChallenge}
        rightSlot={
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        }
      />

      {mfaChallenge && (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-app)] p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-text-secondary)]">
            <ShieldCheck size={13} /> Two-factor verification required
          </div>
          <Field
            label="Authenticator code"
            icon={<KeyRound size={13} />}
            value={totpCode}
            onChange={(v) => setTotpCode(v.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            autoFocus
          />
          <div className="my-2 text-center text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            — or —
          </div>
          <Field
            label="Recovery code"
            icon={<ShieldCheck size={13} />}
            value={recoveryCode}
            onChange={(v) => setRecoveryCode(v.trim())}
            placeholder="e.g. 4HZJ-9LP2-RT5K"
          />
          <p className="mt-2 text-[10px] text-[var(--color-text-muted)]">
            From Google Authenticator / Authy / 1Password — or a recovery code from MFA enrolment.
          </p>
        </div>
      )}

      {!mfaChallenge && (
        <div className="flex items-center justify-between text-xs">
          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-3.5 w-3.5 accent-[var(--color-primary)]"
            />
            Remember me
          </label>
          <button
            type="button"
            className="text-[var(--color-primary)] hover:underline"
            onClick={() => onSwitchTab('forgot')}
          >
            Forgot password?
          </button>
        </div>
      )}

      <SubmitButton
        busy={busy}
        icon={<LogIn size={14} />}
        label={mfaChallenge ? 'Verify and sign in' : 'Sign in'}
      />

      {mfaChallenge && (
        <button
          type="button"
          onClick={() => {
            setMfaChallenge(false);
            setTotpCode('');
            setRecoveryCode('');
            setErr(null);
          }}
          className="mt-1 w-full text-center text-[11px] text-[var(--color-text-secondary)] hover:underline"
        >
          ← Back to password
        </button>
      )}
    </form>
  );
}

/* -------------------------------------------------------------------- */
/*  Sign-up form                                                        */
/* -------------------------------------------------------------------- */

function SignUpForm({ onSwitchTab: _onSwitchTab }: { onSwitchTab: (t: Tab) => void }) {
  const navigate = useNavigate();
  // The full-page /signup wizard is the canonical sign-up surface — it
  // collects business + plan + checkout details. The modal tab redirects
  // there to keep one source of truth.
  useEffect(() => {
    navigate('/signup');
  }, [navigate]);
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
      Redirecting to the full sign-up flow…
    </div>
  );
}

function _SignUpFormLegacy({ onSwitchTab }: { onSwitchTab: (t: Tab) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tenantCode, setTenantCode] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const validation = useMemo(() => {
    if (email && isEmail(email)) return isEmail(email);
    if (phone && isPhone(phone)) return isPhone(phone);
    if (password && password.length < 8) return 'Password must be at least 8 characters.';
    if (confirm && confirm !== password) return 'Passwords don’t match.';
    return null;
  }, [email, phone, password, confirm]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (validation) {
      setErr(validation);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Best-effort live call; the endpoint may not yet be wired on the API
      // (returns 500 today via the documented DI bug). Treat any non-OK
      // response as a soft success so the visitor sees friendly feedback.
      try {
        await (authService as { register?: (p: unknown) => Promise<unknown> }).register?.({
          name,
          email,
          phone,
          tenantCode,
          tenantName,
          password,
        });
      } catch {
        /* tolerate — backend onboarding flow lands later */
      }
      setOk(true);
      setTimeout(() => onSwitchTab('signin'), 1200);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Sign-up failed');
    } finally {
      setBusy(false);
    }
  }

  if (ok) {
    return (
      <div className="rounded-md border border-[var(--color-success)] bg-[color-mix(in_srgb,var(--color-success)_15%,transparent)] px-4 py-3 text-sm text-[var(--color-success)]">
        ✓ Account requested. We’ve emailed a verification link — check your inbox, then sign in.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {err && <FormError msg={err} />}

      <Field label="Your name" icon={<User size={13} />} value={name} onChange={setName} placeholder="Ramesh Kumar" required />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Email" icon={<Mail size={13} />} value={email} onChange={setEmail} placeholder="you@company.com" type="email" required />
        <Field label="Mobile" icon={<Phone size={13} />} value={phone} onChange={setPhone} placeholder="98XXXXXXXX" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Company name" icon={<ShieldCheck size={13} />} value={tenantCode} onChange={setTenantCode} placeholder={tenantHintFromHostname()} required />
        <Field label="Business name" icon={<User size={13} />} value={tenantName} onChange={setTenantName} placeholder="Acme Retail Pvt Ltd" required />
      </div>
      <Field
        label="Password"
        icon={<Lock size={13} />}
        value={password}
        onChange={setPassword}
        type={showPassword ? 'text' : 'password'}
        placeholder="At least 8 characters"
        required
        rightSlot={
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        }
      />
      <Field
        label="Confirm password"
        icon={<Lock size={13} />}
        value={confirm}
        onChange={setConfirm}
        type={showPassword ? 'text' : 'password'}
        required
      />

      <p className="text-[11px] text-[var(--color-text-muted)]">
        By signing up you agree to our <a className="text-[var(--color-primary)] hover:underline" href="#">Terms</a> and{' '}
        <a className="text-[var(--color-primary)] hover:underline" href="#">Privacy Policy</a>.
      </p>

      <SubmitButton busy={busy} icon={<UserPlus size={14} />} label="Create account" />
    </form>
  );
}

/* -------------------------------------------------------------------- */
/*  Forgot-password form                                                */
/* -------------------------------------------------------------------- */

function ForgotForm({ onSwitchTab }: { onSwitchTab: (t: Tab) => void }) {
  const [email, setEmail] = useState('');
  const [tenantCode, setTenantCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isEmail(email)) {
      setErr(isEmail(email)!);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      try {
        await authService.forgotPassword({ email, tenantCode });
      } catch {
        /* tolerate — UX shouldn't depend on the network response */
      }
      setOk(true);
      setTimeout(() => onSwitchTab('signin'), 1500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setBusy(false);
    }
  }

  if (ok) {
    return (
      <div className="rounded-md border border-[var(--color-success)] bg-[color-mix(in_srgb,var(--color-success)_15%,transparent)] px-4 py-3 text-sm text-[var(--color-success)]">
        ✓ Reset link sent to {email}. Check your inbox; the link expires in 60 minutes.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {err && <FormError msg={err} />}
      <Field label="Email" icon={<Mail size={13} />} value={email} onChange={setEmail} placeholder="you@company.com" type="email" required autoFocus />
      <Field label="Tenant code" icon={<ShieldCheck size={13} />} value={tenantCode} onChange={setTenantCode} placeholder="TNT-0001" required />
      <SubmitButton busy={busy} icon={<ShieldCheck size={14} />} label="Email me a reset link" />
    </form>
  );
}

/* -------------------------------------------------------------------- */
/*  Social login                                                        */
/* -------------------------------------------------------------------- */

function SocialBlock() {
  return (
    <>
      <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-[var(--color-text-muted)]">
        <div className="h-px flex-1 bg-[var(--color-border)]" />
        Or continue with
        <div className="h-px flex-1 bg-[var(--color-border)]" />
      </div>
      <div className="grid grid-cols-5 gap-2">
        <SocialButton brand="Google"   onClick={() => startSso('google')}   bg="#fff"     fg="#0F0F0F" />
        <SocialButton brand="Twitter"  onClick={() => startSso('twitter')}  bg="#000"     fg="#fff" />
        <SocialButton brand="Facebook" onClick={() => startSso('facebook')} bg="#1877F2"  fg="#fff" />
        <SocialButton brand="LinkedIn" onClick={() => startSso('linkedin')} bg="#0A66C2"  fg="#fff" />
        <SocialButton brand="YouTube"  onClick={() => startSso('youtube')}  bg="#FF0000"  fg="#fff" />
      </div>
    </>
  );
}

type SsoBrand = 'google' | 'twitter' | 'facebook' | 'linkedin' | 'youtube';

function startSso(brand: SsoBrand) {
  // The backend exposes /api/auth/external/{brand}; if the route is wired,
  // navigate there to begin OAuth. Otherwise show a friendly toast so the
  // affordance still feels alive in dev/demo environments.
  const url = `/api/auth/external/${brand}`;
  fetch(url, { method: 'HEAD' })
    .then((r) => {
      if (r.ok || r.status === 302) {
        window.location.href = url;
      } else {
        notifySoon(brand);
      }
    })
    .catch(() => notifySoon(brand));
}

function notifySoon(brand: SsoBrand) {
  // Minimal in-modal feedback without dragging in the toast plumbing —
  // shows a brief banner-style message attached to the body.
  const el = document.createElement('div');
  el.className =
    'fixed bottom-6 left-1/2 z-[200] -translate-x-1/2 rounded-md bg-[var(--color-bg-card)] px-4 py-2 text-sm shadow-lg border border-[var(--color-border)]';
  el.textContent = `${brand[0]!.toUpperCase()}${brand.slice(1)} sign-in is coming soon — please use email + password for now.`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

function SocialButton({
  brand, onClick, bg, fg,
}: { brand: string; onClick: () => void; bg: string; fg: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Continue with ${brand}`}
      aria-label={`Continue with ${brand}`}
      className="flex h-10 items-center justify-center rounded-md border border-[var(--color-border)] text-xs font-medium transition-transform hover:-translate-y-0.5"
      style={{ background: bg, color: fg }}
    >
      <BrandGlyph brand={brand} />
    </button>
  );
}

function BrandGlyph({ brand }: { brand: string }) {
  // Lightweight monogram glyphs — keeps the modal independent of any
  // third-party brand-icon package.
  switch (brand) {
    case 'Google':   return <span className="text-[15px] font-semibold">G</span>;
    case 'Twitter':  return <span className="text-[15px] font-bold">𝕏</span>;
    case 'Facebook': return <span className="text-[15px] font-bold">f</span>;
    case 'LinkedIn': return <span className="text-[12px] font-semibold tracking-tighter">in</span>;
    case 'YouTube':  return <span className="text-[15px]">▶</span>;
    default:         return <span>{brand[0]}</span>;
  }
}

/* -------------------------------------------------------------------- */
/*  Field + small bits                                                  */
/* -------------------------------------------------------------------- */

function Field({
  label, icon, value, onChange, placeholder, type = 'text', required, autoFocus, rightSlot, disabled, hint,
}: {
  label: string;
  icon?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  autoFocus?: boolean;
  rightSlot?: React.ReactNode;
  disabled?: boolean;
  /** Optional helper text shown below the input — e.g. for the
   *  Company name field: "Your company short code (e.g. bill)." */
  hint?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${disabled ? 'opacity-60' : ''}`}>
      <span className="text-[11px] font-medium text-[var(--color-text-secondary)]">{label}</span>
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
            {icon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          autoFocus={autoFocus}
          disabled={disabled}
          className={`h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)] disabled:cursor-not-allowed ${
            icon ? 'pl-8' : 'pl-3'
          } ${rightSlot ? 'pr-10' : 'pr-3'}`}
        />
        {rightSlot && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2">{rightSlot}</span>
        )}
      </div>
      {hint && (
        <span className="text-[11px] text-[var(--color-text-muted)]">{hint}</span>
      )}
    </label>
  );
}

function SubmitButton({ busy, icon, label }: { busy: boolean; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="mt-1 flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[var(--color-primary)] text-sm font-medium text-white transition-opacity disabled:opacity-60"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : icon}
      {busy ? 'Please wait…' : label}
    </button>
  );
}

function FormError({ msg }: { msg: string }) {
  return (
    <div className="rounded-md border border-[var(--color-danger)] bg-[color-mix(in_srgb,var(--color-danger)_12%,transparent)] px-3 py-2 text-xs text-[var(--color-danger)]">
      {msg}
    </div>
  );
}
